import { Identifier, MatchListType, MatchStateFilter } from '@okletsplay/core';
import { ActionReducerMapBuilder, createAsyncThunk, Draft, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_PAGINATION_SETTINGS } from 'app/constants';
import { DEFAULT_PAGE_SIZE } from 'app/constants/api';
import { EntityStoreName } from 'app/enums/store';
import { Pagination } from 'app/interfaces';
import { PaginatedResponse } from 'app/interfaces/api';
import { MatchEntity } from 'app/interfaces/entity';
import { UpdateEntityRequest } from 'app/interfaces/store';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import { CreateMatchRequest } from 'modules/matches/store/matches/interfaces';
import { store } from 'redux/store';
import { entityAdapter } from './adapter';
import { MatchesState } from './interfaces';
import { MatchStoreStatus } from './enums';

const getRequestParams = (paginationId: string, signal: AbortSignal) => {
  const cancelToken = cancelationSubscribe(signal);
  const {
    limit = DEFAULT_PAGE_SIZE,
    offset = 0,
    reload = true,
  } = store.getState()[EntityStoreName.Matches].pagination[paginationId] ??
  DEFAULT_PAGINATION_SETTINGS;

  return { cancelToken, limit, offset, reload };
};

export const getMatchesList = createAsyncThunk(
  `${EntityStoreName.Matches}/getMatchesList`,
  (
    data: { type?: MatchListType; state?: MatchStateFilter; gameId?: Identifier },
    { signal }
  ): Promise<PaginatedResponse<MatchEntity[]>> => {
    const paginationId = data.gameId ? data.gameId : data.type ?? '';
    const { cancelToken, limit, offset, reload } = getRequestParams(paginationId, signal);
    const params = data?.gameId ? { ...data, pinnedMatchesCount: 6 } : { ...data };

    return apiRequestCall<PaginatedResponse<MatchEntity[]>>({
      params: {
        method: 'GET',
        resource: ApiResource.Matches,
        cancelToken,
        params: {
          limit: reload ? Math.max(offset, limit) : limit,
          offset: reload ? 0 : offset,
          ...params,
        },
      },
    });
  }
);

export const updateMatch = createAsyncThunk(
  `${EntityStoreName.Matches}/updateMatch`,
  (data: UpdateEntityRequest<MatchEntity>, { signal }): Promise<MatchEntity> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<MatchEntity>({
      params: {
        method: 'PATCH',
        resource: ApiResource.Matches,
        cancelToken,
        urlPrefix: data.id,
        data: data.payload,
      },
    });
  }
);

export const getActiveMatchesCount = createAsyncThunk(
  `${EntityStoreName.Matches}/getActiveMatchesCount`,
  (_, { signal }): Promise<number> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<number>({
      params: {
        method: 'GET',
        resource: ApiResource.Matches,
        urlPrefix: 'count',
        cancelToken,
      },
    });
  }
);

export const createMatch = createAsyncThunk(
  `${EntityStoreName.Matches}/addMatch`,
  (data: CreateMatchRequest): Promise<MatchEntity> => {
    return apiRequestCall<MatchEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Matches,
        data,
      },
      successRequestMessage: 'Match has been successfully created.',
    });
  }
);

// TODO: create the base repository with base functionality!
export const updatePaginationReducer = (
  state: MatchesState,
  action: PayloadAction<{ identifier?: string; data: Partial<Pagination> }>
): void => {
  if (!action.payload.identifier) {
    return;
  }

  state.pagination = {
    ...state.pagination,
    [action.payload.identifier]: {
      ...DEFAULT_PAGINATION_SETTINGS,
      ...state.pagination[action.payload.identifier],
      ...action.payload.data,
    },
  };
};

const saveMatches = (
  type: string,
  state: Draft<MatchesState>,
  payload: PaginatedResponse<MatchEntity[]>
) => {
  const reload = state.pagination[type]?.reload ?? true;

  if (reload) {
    entityAdapter.setAll(state, payload.data);
  } else {
    entityAdapter.addMany(state, payload.data);
  }

  if (state.pagination[type]) {
    state.pagination[type].total = payload.total;
    state.pagination[type].offset = reload
      ? payload.data.length
      : state.pagination[type].offset + payload.data.length;
  }
};

export const registerReducers = (builder: ActionReducerMapBuilder<MatchesState>): void => {
  builder
    .addCase(getMatchesList.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, MatchStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getMatchesList.fulfilled, (state, action) => {
      const type = action.meta.arg.type ?? action.meta.arg.gameId ?? '';
      saveMatches(type, state, action.payload);
      entityStoreStatus.removeRequestId(state, MatchStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getMatchesList.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(state, MatchStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getActiveMatchesCount.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        MatchStoreStatus.MatchCountLoading,
        action.meta.requestId
      );
    })
    .addCase(getActiveMatchesCount.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        MatchStoreStatus.MatchCountLoading,
        action.meta.requestId
      );
    })
    .addCase(getActiveMatchesCount.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        MatchStoreStatus.MatchCountLoading,
        action.meta.requestId
      );
    })
    .addCase(createMatch.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, MatchStoreStatus.Creating, action.meta.requestId);
    })
    .addCase(createMatch.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(state, MatchStoreStatus.Creating, action.meta.requestId);
    })
    .addCase(createMatch.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(state, MatchStoreStatus.Creating, action.meta.requestId);
    })
    .addCase(updateMatch.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, MatchStoreStatus.Updating, action.meta.requestId);
    })
    .addCase(updateMatch.fulfilled, (state, action) => {
      if (action.payload) {
        entityAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
      }

      entityStoreStatus.removeRequestId(state, MatchStoreStatus.Updating, action.meta.requestId);
    })
    .addCase(updateMatch.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(state, MatchStoreStatus.Updating, action.meta.requestId);
    });
};
